import type { PipelineCoupleList } from '../constants/pipeline.js'
import type { CompanyPipelineDto, PipelineLostReasonDto, PipelineStageDto, VenuePipelineDto } from '../dto/pipeline.dto.js'
import { isFunnelMainStep } from '../enums/funnel-stage.enum.js'
import { LOST_REASON_COUNT_LABELS } from '../enums/lost-reason.enum.js'
import { couplesBefore, PIPELINE_WHO_REACHED } from './pipeline-story.js'

/**
 * What the Pipeline says in words, shared by the page and the period email
 * (docs/pipeline-view.spec.md, "The email") so the two never word the same
 * thing two ways.
 */

const count = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

/** What a rough amount rounds to: a thousand dollars, in cents. */
const ROUNDING_CENTS = 100_000

/**
 * A dollar amount said roughly, to the nearest thousand: what a gap in bookings
 * is worth, never more precise than it is. Null when it rounds to nothing,
 * which "roughly $0" would misstate.
 */
function roughDollars(cents: number): string | null {
  const rounded = Math.round(cents / ROUNDING_CENTS) * ROUNDING_CENTS
  if (rounded <= 0) return null
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(rounded / 100)
}

/** "about 3", "about 1": a gap in couples, an expectation rather than a count. */
function aboutCouples(n: number): string {
  return `about ${String(Math.max(1, Math.round(n)))}`
}

/** Whose average booking a gap is priced at. */
export type PipelineWhose = 'venue' | 'company'

/** ", roughly $158,000 at this venue’s average booking"; empty when nothing prices it. */
function worthText(gap: number, averageBookingCents: number | null, whose: PipelineWhose): string {
  const worth = averageBookingCents === null ? null : roughDollars(gap * averageBookingCents)
  if (worth === null) return ''
  return `, roughly ${worth} at ${whose === 'venue' ? 'this venue’s' : 'the company’s'} average booking`
}

type FlaggedStage = Pick<PipelineStageDto, 'target' | 'before' | 'usual'>

/**
 * A main step below what's usual, in couples and, for bookings, in what they're
 * worth: "At the usual rate by now, about 5 more of the couples who toured would
 * have booked, roughly $158,000 at this venue’s average booking." Null for a
 * step that isn't below usual.
 */
export function belowUsualSentence(stage: FlaggedStage, averageBookingCents: number | null, whose: PipelineWhose): string | null {
  const u = stage.usual
  const target = stage.target
  if (u === null || u.verdict !== 'below' || !isFunnelMainStep(target)) return null
  const worth = target === 'booked' ? worthText(u.gap, averageBookingCents, whose) : ''
  return `At the usual rate by now, ${aboutCouples(u.gap)} more of ${couplesBefore(stage)} would have ${PIPELINE_WHO_REACHED[target]}${worth}.`
}

/** A main step above what's usual: "Above usual: about 4 more of the couples who were qualified booked a tour than usual by now." */
export function aboveUsualSentence(stage: FlaggedStage): string | null {
  const u = stage.usual
  const target = stage.target
  if (u === null || u.verdict !== 'above' || !isFunnelMainStep(target)) return null
  return `Above usual: ${aboutCouples(-u.gap)} more of ${couplesBefore(stage)} ${PIPELINE_WHO_REACHED[target]} than usual by now.`
}

/**
 * The couples behind a booking that's below usual: those still deciding after
 * their tour, or the lost ones when nobody is. Null when there are none to show.
 */
export function touredNotBooked(lookAt: VenuePipelineDto['lookAt']): { list: PipelineCoupleList; count: number } | null {
  const deciding = lookAt?.touredDeciding ?? 0
  const waiting = deciding + (lookAt?.touredLost ?? 0)
  if (waiting === 0) return null
  return { list: deciding === 0 ? 'toured_lost' : 'toured_deciding', count: waiting }
}

/** One of the lists a Pipeline points at, as it is said. */
export interface PipelineListRow {
  list: PipelineCoupleList
  count: number
  title: string
  detail: string
}

/** What's worth knowing about the couples waiting since their tour: how many never had a proposal, where that's recorded. */
function waitingDetail(noProposal: number | null): string {
  if (noProposal === null || noProposal === 0) return 'Someone owes them a follow-up'
  return `${count.format(noProposal)} of them ${noProposal === 1 ? 'hasn’t' : 'haven’t'} had a proposal`
}

/** Couples to look at (docs/pipeline-view.spec.md): the lists with someone in them, in the order to work them. */
export function lookAtRows(lookAt: NonNullable<VenuePipelineDto['lookAt']>): PipelineListRow[] {
  const rows: (PipelineListRow | null)[] = [
    lookAt.touredWaiting === null
      ? null
      : {
          list: 'toured_waiting',
          count: lookAt.touredWaiting,
          title: 'Toured over 3 weeks ago, no decision yet',
          detail: waitingDetail(lookAt.touredWaitingNoProposal),
        },
    { list: 'holds_ending', count: lookAt.holdsEnding, title: 'Holds that end in the next 7 days', detail: 'Their dates go back on the calendar after that' },
    lookAt.quietBeforeTour === null
      ? null
      : { list: 'quiet_before_tour', count: lookAt.quietBeforeTour, title: 'Stopped responding before a tour', detail: 'In the last 90 days: worth a second try' },
  ]
  return rows.filter((r): r is PipelineListRow => r !== null && r.count > 0)
}

/** The reasons with anyone behind them, most first, "Not recorded" last. */
export function lostReasonsInOrder(reasons: readonly PipelineLostReasonDto[]): PipelineLostReasonDto[] {
  return reasons
    .filter((r) => r.count > 0)
    .sort((a, b) => Number(a.reason === 'not_recorded') - Number(b.reason === 'not_recorded') || b.count - a.count)
}

/** ", most to Stopped responding (21)": the top recorded reason of some losses; empty when none is recorded. */
function mostTo(reasons: readonly PipelineLostReasonDto[], counted: (r: PipelineLostReasonDto) => number): string {
  const top = reasons.filter((r) => r.reason !== 'not_recorded').sort((a, b) => counted(b) - counted(a))[0]
  return top === undefined || counted(top) === 0 ? '' : `, most to ${LOST_REASON_COUNT_LABELS[top.reason]} (${count.format(counted(top))})`
}

/**
 * Losses before and after a tour, where tours are recorded: silence before a
 * tour is a question of follow-up, price after one a question of the sale.
 * Null where nothing records a tour, or nobody was lost.
 */
export function lostBeforeAndAfter(reasons: readonly PipelineLostReasonDto[]): { before: string; after: string } | null {
  const shown = lostReasonsInOrder(reasons)
  const total = shown.reduce((a, r) => a + r.count, 0)
  if (total === 0 || !shown.every((r) => r.afterTour !== null)) return null
  const after = shown.reduce((a, r) => a + (r.afterTour ?? 0), 0)
  return {
    before: `${count.format(total - after)} lost${mostTo(shown, (r) => r.count - (r.afterTour ?? 0))}: a question of speed and follow-up.`,
    after: `${count.format(after)} lost${mostTo(shown, (r) => r.afterTour ?? 0)}: a question of pricing and the sale.`,
  }
}

type VenueRow = Pick<CompanyPipelineDto['rows'][number], 'venueName' | 'values'>

/** A venue's bookings against the days compared with; null where there's nothing to compare. */
export function venueBookedChange(row: VenueRow): number | null {
  const booked = row.values.find((v) => v.target === 'booked')
  return booked?.value == null || booked.previous === null ? null : booked.value - booked.previous
}

/** The venues that need a look first: bookings furthest behind the days compared with, then by name. */
export function venuesLookFirst<R extends VenueRow>(rows: readonly R[]): R[] {
  return [...rows].sort((a, b) => (venueBookedChange(a) ?? Infinity) - (venueBookedChange(b) ?? Infinity) || a.venueName.localeCompare(b.venueName))
}
